import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './AdminCollections.css';
import { getCollections, createCollection, updateCollection, deleteCollection } from '../../firebase/collections';

const CATEGORIES = [
    'Clothing', 'Accessories', 'Printed Materials',
    'Promotional Materials', 'Packaging & Display', 'Full Sublimation',
];

const PAGE_SIZE_OPTIONS = [5, 10, 25, 50];

// ─── Shared tab bar (matches AdminManageProducts style) ───────────────────────
function CollectionsTabBar() {
    const navigate = useNavigate();
    return (
        <div className="ac-top-bar">
            <h1 className="ac-page-title">Collections</h1>
        </div>
    );
}

// ─── Category badge ────────────────────────────────────────────────────────────
function CategoryBadge({ category }) {
    return <span className="ac-category-badge">{category}</span>;
}

// ─── Upload / Edit modal ───────────────────────────────────────────────────────
function CollectionModal({ item, onClose, onSave, saving, saveError }) {
    const isEdit   = !!item;
    const fileRef  = useRef();
    const [form, setForm] = useState({
        title:       item?.title    || '',
        category:    item?.category || '',
        imageFile:   null,
        imagePreview: item?.imageUrl || '',
    });

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => setForm(f => ({ ...f, imageFile: file, imagePreview: ev.target.result }));
        reader.readAsDataURL(file);
    };

    const canSubmit = form.title.trim() && form.category && form.imagePreview;

    return (
        <div className="ac-modal-overlay" onClick={!saving ? onClose : undefined}>
            <div className="ac-modal" onClick={e => e.stopPropagation()}>

                <div className="ac-modal-header">
                    <h2 className="ac-modal-title">{isEdit ? 'Edit Collection' : 'Upload Collection Image'}</h2>
                    <button className="ac-modal-close" onClick={onClose} disabled={saving}>✕</button>
                </div>

                <div className="ac-modal-body">

                    {/* Image upload area */}
                    <div className="ac-upload-area" onClick={() => fileRef.current.click()}>
                        {form.imagePreview ? (
                            <img src={form.imagePreview} alt="preview" className="ac-upload-preview" />
                        ) : (
                            <div className="ac-upload-placeholder">
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.5">
                                    <rect x="3" y="3" width="18" height="18" rx="2"/>
                                    <circle cx="8.5" cy="8.5" r="1.5"/>
                                    <path d="M21 15l-5-5L5 21"/>
                                </svg>
                                <span>Click to upload image</span>
                                <span className="ac-upload-hint">PNG, JPG, WEBP supported</span>
                            </div>
                        )}
                    </div>
                    <input ref={fileRef} type="file" accept="image/*" hidden onChange={handleImageChange} />
                    {form.imagePreview && (
                        <button className="ac-change-img-btn" onClick={() => fileRef.current.click()} disabled={saving}>
                            Change Image
                        </button>
                    )}

                    {/* Title */}
                    <div className="ac-field">
                        <label>Title <span className="ac-required">*</span></label>
                        <input
                            type="text"
                            placeholder="e.g. Polo Shirt for Monimar SPTA"
                            value={form.title}
                            onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                            disabled={saving}
                        />
                    </div>

                    {/* Category */}
                    <div className="ac-field">
                        <label>Category <span className="ac-required">*</span></label>
                        <select
                            value={form.category}
                            onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                            disabled={saving}
                        >
                            <option value="">Select category…</option>
                            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>

                    {saveError && <p className="ac-save-error">{saveError}</p>}

                </div>

                <div className="ac-modal-footer">
                    <button className="ac-btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
                    <button
                        className="ac-btn-primary"
                        onClick={() => onSave(form)}
                        disabled={!canSubmit || saving}
                    >
                        {saving ? (
                            <><span className="ac-spinner" /> {isEdit ? 'Saving…' : 'Uploading…'}</>
                        ) : (
                            isEdit ? 'Save Changes' : 'Upload'
                        )}
                    </button>
                </div>

            </div>
        </div>
    );
}

// ─── Delete confirm dialog ─────────────────────────────────────────────────────
function DeleteDialog({ item, onClose, onConfirm, deleting }) {
    return (
        <div className="ac-modal-overlay" onClick={!deleting ? onClose : undefined}>
            <div className="ac-dialog" onClick={e => e.stopPropagation()}>
                <h3 className="ac-dialog-title">Delete Collection</h3>
                <p className="ac-dialog-body">
                    Are you sure you want to delete <strong>{item.title}</strong>? This cannot be undone.
                </p>
                <div className="ac-dialog-actions">
                    <button className="ac-btn-secondary" onClick={onClose} disabled={deleting}>Cancel</button>
                    <button className="ac-btn-danger" onClick={onConfirm} disabled={deleting}>
                        {deleting ? 'Deleting…' : 'Delete'}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ─── Main component ────────────────────────────────────────────────────────────
export default function AdminCollections() {
    const [collections,   setCollections]   = useState([]);
    const [loading,       setLoading]       = useState(true);
    const [fetchError,    setFetchError]    = useState(null);
    const [search,        setSearch]        = useState('');
    const [page,          setPage]          = useState(1);
    const [pageSize,      setPageSize]      = useState(10);
    const [modal,         setModal]         = useState(null); // null | 'add' | item object
    const [deleteTarget,  setDeleteTarget]  = useState(null);
    const [saving,        setSaving]        = useState(false);
    const [deleting,      setDeleting]      = useState(false);
    const [saveError,     setSaveError]     = useState(null);
    const [actionError,   setActionError]   = useState(null);

    // ── Fetch ────────────────────────────────────────────────────────────────
    const fetchAll = useCallback(async () => {
        setLoading(true);
        setFetchError(null);
        const { collections: items, error } = await getCollections();
        if (error) setFetchError(error);
        else setCollections(items);
        setLoading(false);
    }, []);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    // ── Filter + paginate ────────────────────────────────────────────────────
    const filtered = collections.filter(c => {
        const q = search.toLowerCase();
        return !q || c.title.toLowerCase().includes(q) || c.category.toLowerCase().includes(q);
    });
    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
    const paginated  = filtered.slice((page - 1) * pageSize, page * pageSize);

    // ── Save (create or update) ──────────────────────────────────────────────
    const handleSave = async (form) => {
        setSaving(true);
        setSaveError(null);

        let result;
        if (modal?.id) {
            // Edit
            result = await updateCollection(modal.id, {
                title:            form.title,
                category:         form.category,
                imageFile:        form.imageFile instanceof File ? form.imageFile : null,
                existingImageUrl: modal.imageUrl,
            });
        } else {
            // Create
            result = await createCollection({
                title:     form.title,
                category:  form.category,
                imageFile: form.imageFile,
            });
        }

        setSaving(false);

        if (result.error) {
            setSaveError(result.error);
            return;
        }

        setModal(null);
        setSaveError(null);
        fetchAll();
    };

    // ── Delete ───────────────────────────────────────────────────────────────
    const handleDelete = async () => {
        setDeleting(true);
        setActionError(null);
        const { error } = await deleteCollection(deleteTarget.id);
        setDeleting(false);
        if (error) { setActionError(error); return; }
        setDeleteTarget(null);
        fetchAll();
    };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="ac-page">

            {/* Toolbar */}
            <div className="ac-toolbar">
                <div className="ac-toolbar-left">
                    <div className="ac-entries-label">
                        Show
                        <select className="ac-entries-select" value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}>
                            {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
                        </select>
                        entries
                    </div>
                </div>
                <div className="ac-toolbar-right">
                    <div className="ac-search-wrap">
                        <input
                            type="text"
                            className="ac-search"
                            placeholder="Search collections…"
                            value={search}
                            onChange={e => { setSearch(e.target.value); setPage(1); }}
                        />
                        <svg className="ac-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
                        </svg>
                    </div>
                    <button className="ac-btn-add" onClick={() => { setModal('add'); setSaveError(null); }}>
                        Upload Image
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                        </svg>
                    </button>
                </div>
            </div>

            {/* Error banners */}
            {fetchError  && <div className="ac-banner ac-banner--error">{fetchError}</div>}
            {actionError && <div className="ac-banner ac-banner--error">{actionError}</div>}

            {/* Table */}
            <div className="ac-table-wrap">
                <table className="ac-table">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Preview</th>
                            <th>Title</th>
                            <th>Category</th>
                            <th>Uploaded At</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={6} className="ac-empty">Loading collections…</td></tr>
                        ) : paginated.length === 0 ? (
                            <tr>
                                <td colSpan={6} className="ac-empty">
                                    <div className="ac-empty-state">
                                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.5">
                                            <rect x="3" y="3" width="18" height="18" rx="2"/>
                                            <circle cx="8.5" cy="8.5" r="1.5"/>
                                            <path d="M21 15l-5-5L5 21"/>
                                        </svg>
                                        <span className="ac-empty-title">No collection images yet</span>
                                        <span className="ac-empty-sub">Click "Upload Image" to add your first collection photo.</span>
                                    </div>
                                </td>
                            </tr>
                        ) : paginated.map((item, idx) => (
                            <tr key={item.id}>
                                <td className="ac-cell-num">{(page - 1) * pageSize + idx + 1}</td>
                                <td>
                                    <img src={item.imageUrl} alt={item.title} className="ac-thumb" />
                                </td>
                                <td className="ac-cell-title">{item.title}</td>
                                <td><CategoryBadge category={item.category} /></td>
                                <td className="ac-cell-date">{item.uploadedAt || '—'}</td>
                                <td>
                                    <div className="ac-actions">
                                        <button
                                            className="ac-action-btn ac-action-btn--edit"
                                            onClick={() => { setModal(item); setSaveError(null); }}
                                            title="Edit"
                                        >
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                            </svg>
                                        </button>
                                        <button
                                            className="ac-action-btn ac-action-btn--delete"
                                            onClick={() => { setDeleteTarget(item); setActionError(null); }}
                                            title="Delete"
                                        >
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <polyline points="3 6 5 6 21 6"/>
                                                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                                                <path d="M10 11v6M14 11v6"/>
                                                <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                                            </svg>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            <div className="ac-pagination">
                <span className="ac-result-count">
                    {filtered.length === 0 ? 'No results' : `Showing ${Math.min((page - 1) * pageSize + 1, filtered.length)}–${Math.min(page * pageSize, filtered.length)} of ${filtered.length}`}
                </span>
                <button className="ac-page-btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Previous</button>
                <span className="ac-page-divider">|</span>
                <button className="ac-page-btn" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next →</button>
            </div>

            {/* Upload / Edit modal */}
            {modal && (
                <CollectionModal
                    item={modal === 'add' ? null : modal}
                    onClose={() => !saving && setModal(null)}
                    onSave={handleSave}
                    saving={saving}
                    saveError={saveError}
                />
            )}

            {/* Delete dialog */}
            {deleteTarget && (
                <DeleteDialog
                    item={deleteTarget}
                    onClose={() => !deleting && setDeleteTarget(null)}
                    onConfirm={handleDelete}
                    deleting={deleting}
                />
            )}
        </div>
    );
}